'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { BaseLinePause } from '@/app/components/icons/BaseLinePause';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { VoiceSelectionDialog } from '@/app/components/shared/VoiceSelectionDialog';
import { IMG_DEFAULT } from '@/app/constants';
import { useAudioPlay } from '@/app/hooks';
import { cn } from '@/app/utils';

interface SelectedVoice {
  id: string;
  name: string;
  url: string;
  image: string;
}

function VoicePreviewButton({ voice }: { voice: SelectedVoice }) {
  const tPlayer = useTranslations('tools.voiceClone.player');
  const { isPlaying, handlePlayPause } = useAudioPlay({
    id: `offline-avatar-speech-preview:${voice.id}`,
    url: voice.url,
  });

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        handlePlayPause();
      }}
      className="relative size-8 shrink-0 overflow-hidden rounded-full bg-system-white08 text-system-white"
      aria-label={tPlayer(isPlaying ? 'pause' : 'play')}
    >
      <img
        src={voice.image || IMG_DEFAULT}
        alt=""
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 size-full object-cover',
          isPlaying && 'animate-[spin_4s_linear_infinite]',
        )}
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/40 transition-colors hover:bg-black/55">
        {isPlaying ? <BaseLinePause className="size-4" /> : <BaseLinePlay className="size-4" />}
      </span>
    </button>
  );
}

export function StudioVoiceSelector({
  voice,
  onVoiceChange,
  locked = false,
  onLocked,
}: {
  voice: string;
  onVoiceChange: (voice: string) => void;
  locked?: boolean;
  onLocked?: () => void;
}) {
  const t = useTranslations('tools.offlineAvatar.avatar');
  const [open, setOpen] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<SelectedVoice | null>(null);

  useEffect(() => {
    if (!voice) setSelectedVoice(null);
  }, [voice]);

  const handleResolvedVoice = useCallback((resolvedVoice: { id: string; name: string; url?: string; img?: string }) => {
    setSelectedVoice((current) => {
      const next = {
        id: resolvedVoice.id,
        name: resolvedVoice.name,
        url: resolvedVoice.url || '',
        image: resolvedVoice.img || '',
      };
      return current?.id === next.id &&
        current.name === next.name &&
        current.url === next.url &&
        current.image === next.image
        ? current
        : next;
    });
  }, []);

  return (
    <div className="flex min-w-0 items-center gap-3">
      <p className="shrink-0 text-sm text-white/60">{t('voiceLabel')}</p>
      <div className="w-full min-w-0 max-w-[360px]">
        <VoiceSelectionDialog
          open={locked ? false : open}
          onOpenChange={(nextOpen) => {
            if (locked && nextOpen) {
              onLocked?.();
              return;
            }
            setOpen(nextOpen);
          }}
          title={t('chooseVoice')}
          showCustomVoices={false}
          value={voice}
          onConfirm={(nextVoiceId, nextVoice) => {
            if (locked) {
              onLocked?.();
              return;
            }
            onVoiceChange(nextVoiceId);
            setSelectedVoice(
              nextVoice
                ? {
                    id: nextVoiceId,
                    name: nextVoice.name,
                    url: nextVoice.url || '',
                    image: nextVoice.img || '',
                  }
                : null,
            );
          }}
          onResolvedVoice={handleResolvedVoice}
          voiceInfoEditFieldSuffix="offline-avatar-speech"
          trigger={
            <div
              className={cn(
                'flex h-12 w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 text-sm transition-colors hover:border-white/20',
                selectedVoice ? 'text-white' : 'text-white/50 hover:text-white/70',
              )}
            >
              {selectedVoice?.url ? <VoicePreviewButton voice={selectedVoice} /> : null}
              <button type="button" className="flex min-w-0 flex-1 items-center gap-3">
                <span className="min-w-0 flex-1 truncate text-left">{selectedVoice?.name || t('chooseVoice')}</span>
                <BaseLineRight className="size-4 shrink-0 text-white/40" />
              </button>
            </div>
          }
        />
      </div>
    </div>
  );
}
